import * as Sentry from '@sentry/react';
import { isDemoMode } from '@utils/demoMode';

let environment;
switch (window.location.hostname) {
  case 'dev.app.jooice.com':
    environment = 'dev';
    break;
  case 'staging.app.jooice.com':
    environment = 'staging';
    break;
  case 'app.jooice.com':
    environment = 'prod';
    break;
  default:
    environment = 'local';
    break;
}

// --- Flood protection -------------------------------------------------------
// Rate-cap any single error type+message so a runaway render/effect loop can't
// spam Sentry and contribute to the shared org error quota (see the June 2026
// backend incident). Browser-side insurance: FE is not currently a flood source,
// but this also bounds existing repetitive noise (e.g. duplicated 'Error: Rejected').
const FLOOD_WINDOW_MS = 60_000;
const FLOOD_MAX_PER_WINDOW = 5;
const floodCounts = new Map<string, { count: number; windowStart: number }>();

function floodKey(event: Sentry.ErrorEvent): string {
  const ex = event.exception?.values?.[0];
  return `${ex?.type ?? event.message ?? 'unknown'}|${(ex?.value ?? '').slice(0, 60)}`;
}

// Demo mode runs on fabricated data: its errors are not product errors, and they would
// consume the shared org quota alongside real ones.
if (!isDemoMode()) {
  Sentry.init({
    dsn:
      import.meta.env.VITE_SENTRY_DSN ||
      'https://42a4c76eeb7c1166257ff1a0e8e7eeff@o4508799967035392.ingest.de.sentry.io/4509354527817808',
    // Setting this option to false prevents default PII data from being sent to Sentry.
    sendDefaultPii: false,
    environment,
    release: import.meta.env.PACKAGE_VERSION,
    integrations: [
      Sentry.browserTracingIntegration({ enableInp: true }),
      // Session Replay deliberately excluded: this product is HIPAA-adjacent and Replay's network/canvas capture is not safe by default without a signed BAA - revisit only after that is in place.
    ],
    tracesSampleRate: environment === 'local' || isDemoMode() ? 0 : 0.2,
    tracePropagationTargets: [/^https:\/\/(dev\.)?[a-z0-9-]+\.api\.jooice\.net/],

    beforeSend(event) {
      // Rate-cap a single error type+message to FLOOD_MAX_PER_WINDOW per minute.
      const now = Date.now();
      if (floodCounts.size > 1000) floodCounts.clear(); // bound memory
      const key = floodKey(event);
      const entry = floodCounts.get(key);
      if (!entry || now - entry.windowStart > FLOOD_WINDOW_MS) {
        floodCounts.set(key, { count: 1, windowStart: now });
        return event;
      }
      entry.count += 1;
      return entry.count > FLOOD_MAX_PER_WINDOW ? null : event;
    },
  });
}
